import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import { getCreatableCourseTypes } from '../utils/coursePermissions';
import { useToast } from '../context/ToastContext';

const CourseForm = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const isTemplateMode = searchParams.get('template') === '1';

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const allowedTypes = getCreatableCourseTypes(user, !!user?.managesDepartment);
  const allow = (t) => allowedTypes.has(t);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    requiresTest: true,
    showCorrectAnswers: true,
    videoUrl: '',
    durationValue: '',
    durationUnit: 'ore',
    minTrainingValue: '',
    minTrainingUnit: 'min',
    testTimeLimit: '', // limită timp test (minute) — opțional; gol = fără cronometru
    isTemplate: isTemplateMode,
  });

  // Doar responsabilii SSM/PSI pot publica template-uri
  const canPublishTemplate =
    ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) ||
    !!user?.isSSMInstructor ||
    !!user?.isCadruTehnicPSI;

  useEffect(() => {
    if (isEdit) fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await coursesAPI.getById(id);
      const course = response.data;
      const initTime = (storedMin) => {
        if (!storedMin) return { value: '', unit: 'min' };
        if (storedMin >= 60 && storedMin % 60 === 0)
          return { value: (storedMin / 60).toString(), unit: 'ore' };
        return { value: storedMin.toString(), unit: 'min' };
      };
      // Durata pe fișă se exprimă DOAR în ore (durata legală).
      const initDuration = (storedMin) => {
        if (!storedMin) return { value: '', unit: 'ore' };
        // Convertim din minute stocate în ore (acceptă și valori fracționare, ex: 90min -> 1.5h)
        const hours = storedMin / 60;
        return {
          value: (Number.isInteger(hours) ? hours : parseFloat(hours.toFixed(2))).toString(),
          unit: 'ore',
        };
      };
      const dur = initDuration(course.duration);
      const minT = initTime(course.minTrainingTime);
      setFormData({
        title: course.title,
        description: course.description || '',
        type: course.type,
        requiresTest: course.requiresTest !== false,
        showCorrectAnswers: course.showCorrectAnswers !== false,
        videoUrl: course.videoUrl || '',
        durationValue: dur.value,
        durationUnit: dur.unit,
        minTrainingValue: minT.value,
        minTrainingUnit: minT.unit,
        testTimeLimit: course.testTimeLimit ? String(course.testTimeLimit) : '',
        isTemplate: !!course.isTemplate,
      });
    } catch (err) {
      showToast(t('courseForm.loadError'), 'error');
      navigate('/courses');
    }
  };

  const isIP = formData.type === 'IP_SSM' || formData.type === 'IP_SU';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const toMinutes = (val, unit) => {
        if (!val) return null;
        return Math.round(unit === 'ore' ? parseFloat(val) * 60 : parseFloat(val));
      };
      const payload = {
        title: formData.title,
        type: formData.type,
        requiresTest: formData.requiresTest,
        showCorrectAnswers: formData.showCorrectAnswers,
        ...(isEdit && {
          description: formData.description,
          videoUrl: formData.videoUrl,
          duration: toMinutes(formData.durationValue, formData.durationUnit),
          minTrainingTime: toMinutes(formData.minTrainingValue, formData.minTrainingUnit),
          testTimeLimit: formData.testTimeLimit ? parseInt(formData.testTimeLimit) : null,
        }),
      };
      let savedId;
      if (isEdit) {
        await coursesAPI.update(id, payload);
        savedId = id;
      } else {
        const res = await coursesAPI.create(payload);
        savedId = res?.data?.id || res?.id;
      }
      // Toggle template status separat (endpoint dedicat — verifică drepturi)
      if (canPublishTemplate && savedId) {
        try {
          await coursesAPI.toggleTemplate(savedId, formData.isTemplate);
        } catch (_) {}
      }
      // După save (create sau edit), ducem utilizatorul la pagina de conținut ca să
      // poată continua cu module + test fără să redeschidă cursul manual.
      navigate(savedId ? `/courses/${savedId}/content` : '/courses');
    } catch (err) {
      showToast(t('courseForm.saveError'), 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/courses')}
          className="inline-flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('common.back')}
        </button>
      </div>

      <Card className="p-8">
        <h1 className="page-title mb-2">
          {isEdit
            ? t('courses.editCourse')
            : isTemplateMode
              ? t('courses.newTemplate')
              : t('courses.newCourse')}
        </h1>
        {!isEdit && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {isTemplateMode ? t('courseForm.templateModeSubtitle') : t('courses.subtitle')}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label={t('courses.courseTitle') + ' *'}
            name="title"
            required
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            placeholder=""
          />

          <div>
            <label className="input-label">{t('courses.courseType')} *</label>
            <select
              name="type"
              required
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                // Testul obligatoriu rămâne pre-bifat DOAR pentru IIG (introductiv-generală).
                // Pentru ILM, IP și suplimentare e debifat implicit — dacă instructorul vrea test
                // și la ILM, îl bifează manual și îl configurează (atunci apare și pe fișă).
                const newIsIIG = newType === 'IIG_SSM' || newType === 'IIG_SU';
                setFormData((prev) => ({ ...prev, type: newType, requiresTest: newIsIIG }));
              }}
              className="input"
            >
              <option value="">{t('courseForm.chooseCategory')}</option>
              <optgroup label="SSM">
                {allow('IIG_SSM') && <option value="IIG_SSM">{t('courses.type.IIG_SSM')}</option>}
                {allow('ILM_SSM') && <option value="ILM_SSM">{t('courses.type.ILM_SSM')}</option>}
                {allow('IP_SSM') && <option value="IP_SSM">{t('courses.type.IP_SSM')}</option>}
                {allow('SUPLIMENTARE_SSM') && (
                  <option value="SUPLIMENTARE_SSM">{t('courses.type.SUPLIMENTARE_SSM')}</option>
                )}
              </optgroup>
              <optgroup label={t('courseForm.emergencySituations')}>
                {allow('IIG_SU') && <option value="IIG_SU">{t('courses.type.IIG_SU')}</option>}
                {allow('ILM_SU') && <option value="ILM_SU">{t('courses.type.ILM_SU')}</option>}
                {allow('IP_SU') && <option value="IP_SU">{t('courses.type.IP_SU')}</option>}
              </optgroup>
            </select>
            {isIP && (
              <p className="text-xs text-amber-600 dark:text-amber-300 mt-1.5 flex items-center gap-1">
                <svg
                  className="w-3.5 h-3.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t('courseForm.periodicNoTest')}
              </p>
            )}
          </div>

          {/* Requires test — always visible */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="requiresTest"
              checked={formData.requiresTest !== false}
              onChange={(e) => setFormData((prev) => ({ ...prev, requiresTest: e.target.checked }))}
              className="w-4 h-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
            />
            <label
              htmlFor="requiresTest"
              className="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('courses.requiresTest')}
              <span className="block text-xs text-slate-400 dark:text-slate-400 font-normal">
                {t('courses.requiresTestHelp')}
              </span>
            </label>
          </div>

          {/* Publică ca template — vizibil doar responsabililor SSM/PSI */}
          {canPublishTemplate && (
            <div className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">
              <input
                type="checkbox"
                id="isTemplate"
                checked={!!formData.isTemplate}
                onChange={(e) => setFormData((prev) => ({ ...prev, isTemplate: e.target.checked }))}
                className="w-4 h-4 text-purple-600 rounded border-purple-300 dark:border-purple-700 mt-0.5"
              />
              <label
                htmlFor="isTemplate"
                className="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {t('courseForm.publishAsTemplate')}
                <span className="block text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">
                  {t('courseForm.publishAsTemplateDesc')}
                </span>
              </label>
            </div>
          )}

          {/* Arată răspunsuri corecte — disponibil și la creare, nu doar la edit */}
          {formData.requiresTest && (
            <div className="flex items-center gap-3 ml-7">
              <input
                type="checkbox"
                id="showCorrectAnswers"
                checked={formData.showCorrectAnswers !== false}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, showCorrectAnswers: e.target.checked }))
                }
                className="w-4 h-4 text-primary-600 rounded border-slate-300 dark:border-slate-700"
              />
              <label
                htmlFor="showCorrectAnswers"
                className="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {t('courses.showCorrectAnswers')}
              </label>
            </div>
          )}

          {/* Restul câmpurilor avansate (descriere, video, durată) — doar în edit */}
          {isEdit && (
            <>
              <div>
                <label className="input-label">{t('courses.description')}</label>
                <textarea
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="input"
                  placeholder=""
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">{t('courses.duration')} *</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      required
                      value={formData.durationValue}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, durationValue: e.target.value }))
                      }
                      className="input flex-1"
                      placeholder="ex: 8"
                    />
                    <select
                      value={formData.durationUnit}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, durationUnit: e.target.value }))
                      }
                      className="input w-24"
                    >
                      <option value="ore">ore</option>
                    </select>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                    {t('courses.durationHelp')}
                  </p>
                </div>
                <div>
                  <label className="input-label">{t('courses.minTrainingTime')}</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.minTrainingValue}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, minTrainingValue: e.target.value }))
                      }
                      className="input flex-1"
                      placeholder="ex: 30"
                    />
                    <select
                      value={formData.minTrainingUnit}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, minTrainingUnit: e.target.value }))
                      }
                      className="input w-24"
                    >
                      <option value="min">min</option>
                      <option value="ore">ore</option>
                    </select>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                    {t('courses.minTrainingTimeHelp')}
                  </p>
                </div>
              </div>

              {/* Limită timp test — OPȚIONAL. Gol = testul NU are cronometru (fără presiune de timp). */}
              <div className="mt-4">
                <label className="input-label">{t('courseForm.testTimeLimitLabel')}</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.testTimeLimit}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, testTimeLimit: e.target.value }))
                  }
                  className="input"
                  placeholder={t('courseForm.testTimeLimitPlaceholder')}
                />
                <p className="text-xs text-slate-400 dark:text-slate-400 mt-1">
                  {t('courseForm.testTimeLimitHelp')}
                </p>
              </div>
            </>
          )}

          {/* #4: la creare, setăm așteptarea — durata/descrierea/materialele vin la pasul următor. */}
          {!isEdit && (
            <div className="p-3 rounded-lg border border-blue-200 dark:border-slate-700 bg-blue-50/60 dark:bg-slate-800/60 text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{t('courses.createNextStepHint')}</span>
            </div>
          )}
          <div className="flex gap-4 pt-2">
            <Button type="submit" loading={loading} size="lg" className="flex-1">
              {loading
                ? t('common.loading')
                : isEdit
                  ? t('common.saveChanges')
                  : isTemplateMode
                    ? t('courses.createTemplateAndContinue')
                    : t('courses.createAndContinue')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={() => navigate('/courses')}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CourseForm;
